import React, { useState, useCallback, useEffect } from 'react'
import axios from 'axios'
import {
  IconClose, IconPhone, IconChat, IconMail, IconPhoneIncoming,
  IconGear, IconCheck, IconWarning, IconPeople, IconBell
} from './icons'

// ─── Utilities ──────────────────────────────────────────────

function formatAge(dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function formatDuration(seconds) {
  if (!seconds) return ''
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

// ─── Tab Navigation ─────────────────────────────────────────

const TABS = [
  { key: 'inbox', label: 'Inbox', icon: IconChat },
  { key: 'presence', label: 'Presence', icon: IconPeople },
  { key: 'config', label: 'Configuration', icon: IconGear },
]

function TabBar({ active, onChange, counts }) {
  return (
    <div className="comm-drawer-tabs">
      {TABS.map(tab => {
        const Ic = tab.icon
        const count = counts[tab.key] || 0
        return (
          <button
            key={tab.key}
            className={`comm-drawer-tab ${active === tab.key ? 'active' : ''}`}
            onClick={() => onChange(tab.key)}
          >
            <Ic size={14} />
            <span>{tab.label}</span>
            {count > 0 && <span className="comm-tab-badge">{count}</span>}
          </button>
        )
      })}
    </div>
  )
}

// ─── Inbox Tab ──────────────────────────────────────────────

function InboxTab({ commData }) {
  const { unreadSms = [], voicemails = [], missedCalls = [] } = commData || {}

  return (
    <div className="comm-drawer-scroll">
      {/* SMS Section */}
      <div className="comm-drawer-section">
        <div className="comm-drawer-section-head">
          <IconChat size={14} />
          <span>Unread SMS</span>
          {unreadSms.length > 0 && <span className="comm-tab-badge">{unreadSms.length}</span>}
        </div>
        {unreadSms.length === 0 ? (
          <div className="comm-drawer-empty">No unread SMS messages</div>
        ) : (
          <div className="comm-drawer-list">
            {unreadSms.map(m => (
              <div key={m.id} className="comm-drawer-item">
                <div className="comm-drawer-item-avatar">
                  <IconChat size={14} />
                </div>
                <div className="comm-drawer-item-body">
                  <div className="comm-drawer-item-head">
                    <span className="comm-drawer-item-name">{m.fromName || m.from}</span>
                    <span className="comm-drawer-item-time">{formatAge(m.receivedAt)}</span>
                  </div>
                  <div className="comm-drawer-item-number">{m.from}</div>
                  <div className="comm-drawer-item-preview">{m.text}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Voicemails Section */}
      <div className="comm-drawer-section">
        <div className="comm-drawer-section-head">
          <IconMail size={14} />
          <span>Voicemails</span>
          {voicemails.length > 0 && <span className="comm-tab-badge">{voicemails.length}</span>}
        </div>
        {voicemails.length === 0 ? (
          <div className="comm-drawer-empty">No pending voicemails</div>
        ) : (
          <div className="comm-drawer-list">
            {voicemails.map(vm => (
              <div key={vm.id} className="comm-drawer-item">
                <div className="comm-drawer-item-avatar">
                  <IconMail size={14} />
                </div>
                <div className="comm-drawer-item-body">
                  <div className="comm-drawer-item-head">
                    <span className="comm-drawer-item-name">{vm.fromName || vm.from}</span>
                    <span className="comm-drawer-item-time">{formatAge(vm.receivedAt)}</span>
                  </div>
                  <div className="comm-drawer-item-number">{vm.from} &middot; {formatDuration(vm.duration)}</div>
                  {vm.transcription && (
                    <div className="comm-drawer-item-preview">{vm.transcription}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Missed Calls Section */}
      <div className="comm-drawer-section">
        <div className="comm-drawer-section-head">
          <IconPhoneIncoming size={14} />
          <span>Missed Calls</span>
          {missedCalls.length > 0 && <span className="comm-tab-badge">{missedCalls.length}</span>}
        </div>
        {missedCalls.length === 0 ? (
          <div className="comm-drawer-empty">No missed calls in the last 24h</div>
        ) : (
          <div className="comm-drawer-list">
            {missedCalls.map((c, i) => (
              <div key={i} className="comm-drawer-item">
                <div className="comm-drawer-item-avatar missed">
                  <IconPhoneIncoming size={14} />
                </div>
                <div className="comm-drawer-item-body">
                  <div className="comm-drawer-item-head">
                    <span className="comm-drawer-item-name">{c.fromName || c.from}</span>
                    <span className="comm-drawer-item-time">{formatAge(c.time)}</span>
                  </div>
                  <div className="comm-drawer-item-number">{c.from}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Presence Tab ───────────────────────────────────────────

function PresenceTab({ presence = [] }) {
  const statusColor = (p) => {
    if (p.onCall) return 'var(--risk, #e53e3e)'
    if (p.dnd) return 'var(--ink-4, #a0a0a0)'
    if (p.status === 'Busy') return 'var(--risk, #e53e3e)'
    if (p.status === 'Available') return 'var(--ok, #38a169)'
    if (p.status === 'Away') return 'var(--warn, #d69e2e)'
    return 'var(--ink-4, #a0a0a0)'
  }

  const statusLabel = (p) => {
    if (p.onCall) return `On call (${p.callDirection || 'active'})`
    if (p.dnd) return 'Do Not Disturb'
    return p.status || 'Offline'
  }

  const initials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase().slice(0, 2)
  }

  if (!presence.length) {
    return (
      <div className="comm-drawer-scroll">
        <div className="comm-drawer-empty-state">
          <IconPeople size={32} />
          <div className="comm-drawer-empty-title">No Presence Data</div>
          <div className="comm-drawer-empty-desc">
            RingCentral presence requires an active connection and the presence webhook subscription.
            Check the Configuration tab to set up webhooks.
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="comm-drawer-scroll">
      <div className="comm-drawer-section">
        <div className="comm-drawer-section-head">
          <IconPeople size={14} />
          <span>Team Presence ({presence.length})</span>
        </div>
        <div className="comm-drawer-presence-grid">
          {presence.map((p, i) => (
            <div key={p.extensionId || i} className="comm-drawer-presence-card">
              <div className="presence-card-avatar" style={{ borderColor: statusColor(p) }}>
                <span>{initials(p.name)}</span>
              </div>
              <div className="presence-card-name">{p.name}</div>
              <div className="presence-card-status" style={{ color: statusColor(p) }}>
                <span className="presence-status-dot" style={{ background: statusColor(p) }} />
                {statusLabel(p)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Config Tab ─────────────────────────────────────────────

function ConfigTab({ rcConfig, url, onRefresh }) {
  const [setupLoading, setSetupLoading] = useState(false)
  const [setupResult, setSetupResult] = useState(null)

  const handleSetupWebhooks = useCallback(async () => {
    setSetupLoading(true)
    setSetupResult(null)
    try {
      const res = await axios.post(`${url}/api/hmi/dashboard/rc-setup-webhooks`)
      setSetupResult(res.data)
      if (onRefresh) setTimeout(onRefresh, 1500)
    } catch (err) {
      setSetupResult({ success: false, message: err.response?.data?.message || err.message })
    } finally {
      setSetupLoading(false)
    }
  }, [url, onRefresh])

  if (!rcConfig) {
    return (
      <div className="comm-drawer-scroll">
        <div className="comm-drawer-empty">Loading configuration...</div>
      </div>
    )
  }

  const ENV_HELP = {
    RC_CLIENTID: 'RingCentral app Client ID — from RC Developer Portal → your app → Credentials',
    RC_CLIENTSECRET: 'RingCentral app Client Secret — same location as Client ID',
    RC_JWT: 'JWT token for server auth — RC Admin Portal → user → Credentials → Create JWT. Expires periodically — regenerate when you see "Unparseable assertion"',
    RC_WEBHOOK_BASE_URL: 'Public HTTPS URL where RC sends webhook POSTs. Must be reachable from the internet. Falls back to PORTAL_URL if not set. For local dev use ngrok.',
    RC_WEBHOOK_SECRET: 'Any random string you create — used to verify incoming webhooks are genuine. Set same value here and in RC subscription.',
    PORTAL_URL: 'Your app\'s base URL — used as fallback for webhook URL. Should be HTTPS in production.'
  }

  const envEntries = rcConfig.env ? Object.entries(rcConfig.env) : []

  return (
    <div className="comm-drawer-scroll">
      {/* Connection Status */}
      <div className="comm-drawer-section">
        <div className="comm-drawer-section-head">
          <IconBell size={14} />
          <span>Connection Status</span>
        </div>
        <div style={{ padding: '8px 16px' }}>
          <div className="comm-config-status-large">
            {rcConfig.connected ? (
              <div className="comm-status-banner ok">
                <IconCheck size={16} />
                <div>
                  <div className="comm-status-title">Connected to RingCentral</div>
                  <div className="comm-status-sub">{rcConfig.extensions?.length || 0} extensions detected</div>
                </div>
              </div>
            ) : (
              <div className="comm-status-banner error">
                <IconWarning size={16} />
                <div>
                  <div className="comm-status-title">Disconnected</div>
                  <div className="comm-status-sub">{rcConfig.error || 'Unable to connect to RingCentral'}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Environment Variables */}
      <div className="comm-drawer-section">
        <div className="comm-drawer-section-head">
          <IconGear size={14} />
          <span>Environment Variables</span>
        </div>
        <div className="comm-drawer-config-table">
          {envEntries.map(([key, val]) => (
            <div key={key} className="comm-drawer-env-row">
              <div className="comm-drawer-config-row">
                <span className="comm-drawer-config-key">{key}</span>
                <span className={`comm-drawer-config-val ${val ? 'set' : 'missing'}`}>
                  {val || 'NOT SET'}
                </span>
              </div>
              {ENV_HELP[key] && (
                <div className="comm-drawer-env-help">{ENV_HELP[key]}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Extensions */}
      {rcConfig.extensions?.length > 0 && (
        <div className="comm-drawer-section">
          <div className="comm-drawer-section-head">
            <IconPhone size={14} />
            <span>Extensions ({rcConfig.extensions.length})</span>
          </div>
          <div className="comm-drawer-config-table">
            {rcConfig.extensions.map(ext => (
              <div key={ext.id} className="comm-drawer-config-row">
                <span className="comm-drawer-config-key">Ext {ext.extensionNumber}</span>
                <span className="comm-drawer-config-val set">{ext.name} &middot; {ext.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Webhook Subscriptions */}
      <div className="comm-drawer-section">
        <div className="comm-drawer-section-head">
          <IconBell size={14} />
          <span>Webhook Subscriptions ({rcConfig.webhooks?.length || 0})</span>
        </div>
        {rcConfig.webhooks?.length > 0 ? (
          <div className="comm-drawer-config-table">
            {rcConfig.webhooks.map(wh => {
              const filterLabel = (wh.eventFilters || []).map(f => {
                if (f.includes('message-store')) return 'SMS'
                if (f.includes('presence')) return 'Presence'
                if (f.includes('telephony')) return 'Telephony'
                return f.split('/').pop()
              }).join(', ')
              return (
                <div key={wh.id} className="comm-drawer-config-row">
                  <span className="comm-drawer-config-key">{filterLabel}</span>
                  <span className={`comm-drawer-config-val ${wh.status === 'Active' ? 'set' : 'missing'}`}>
                    {wh.status}
                  </span>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="comm-drawer-empty">No webhook subscriptions active</div>
        )}

        <div style={{ padding: '12px 16px' }}>
          <button
            className="dbtn dbtn-accent"
            style={{ width: '100%', height: 38, fontSize: 13 }}
            onClick={handleSetupWebhooks}
            disabled={setupLoading || !rcConfig.connected}
          >
            {setupLoading ? 'Setting up webhooks...' : 'Setup All Webhooks (SMS + Presence + Telephony)'}
          </button>
          {!rcConfig.connected && (
            <div style={{ fontSize: 11, color: 'var(--risk)', marginTop: 6, textAlign: 'center' }}>
              Fix RC credentials before setting up webhooks. The RC_JWT token may be expired &mdash; regenerate it from the RingCentral admin portal.
            </div>
          )}
        </div>

        {setupResult && (
          <div style={{ padding: '0 16px 12px' }}>
            <div className="comm-drawer-setup-result">
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                {setupResult.success ? 'All webhooks configured' : 'Some webhooks failed'}
              </div>
              {setupResult.data && Object.entries(setupResult.data).map(([key, val]) => (
                <div key={key} className="comm-drawer-config-row" style={{ padding: '2px 0' }}>
                  <span className="comm-drawer-config-key" style={{ textTransform: 'capitalize' }}>{key}</span>
                  <span className={`comm-drawer-config-val ${val.success ? 'set' : 'missing'}`}>
                    {val.success ? val.action : val.error}
                  </span>
                </div>
              ))}
              {setupResult.message && !setupResult.data && (
                <div style={{ fontSize: 11, color: 'var(--risk)' }}>{setupResult.message}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Drawer ────────────────────────────────────────────

export default function CommDrawer({ open, onClose, commData, rcConfig, url, onRefreshConfig }) {
  const [activeTab, setActiveTab] = useState('inbox')

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handleKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  const summary = commData?.summary || {}
  const tabCounts = {
    inbox: summary.totalUnread || 0,
    presence: commData?.presence?.length || 0,
    config: 0,
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`comm-drawer-backdrop ${open ? 'open' : ''}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`comm-drawer ${open ? 'open' : ''}`}>
        {/* Header */}
        <div className="comm-drawer-header">
          <div className="comm-drawer-header-left">
            <IconPhone size={18} />
            <div>
              <div className="comm-drawer-title">Communications</div>
              <div className="comm-drawer-subtitle">RingCentral VoIP &middot; {rcConfig?.connected ? 'Connected' : 'Disconnected'}</div>
            </div>
          </div>
          <button className="comm-drawer-close" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        {/* Tabs */}
        <TabBar active={activeTab} onChange={setActiveTab} counts={tabCounts} />

        {/* Tab Content */}
        {activeTab === 'inbox' && <InboxTab commData={commData} />}
        {activeTab === 'presence' && <PresenceTab presence={commData?.presence} />}
        {activeTab === 'config' && <ConfigTab rcConfig={rcConfig} url={url} onRefresh={onRefreshConfig} />}
      </div>
    </>
  )
}
